'use client'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { usePlpProductListProvider } from 'plp/context/PlpProductListProvider/usePlpProductListProvider'
import { productListEvents } from 'plp/utils/analyticsEvents'
import type { Product } from 'product/types'
import { ProductList } from 'product-list/components/ProductList/ProductList'
import { ProductListAnalyticsProvider } from 'product-list/context/ProductListAnalyticsProvider/ProductListAnalyticsProvider'
import type { UseFiltersData } from 'product-list/types/Filters'
import type {
	ProductListFilters,
	ProductListGroup,
	ProductListParams,
	RenderedProductListItem,
} from 'product-list/types/ProductsList'
import { applyOverlayAbTestSettings } from 'product-list/utils/applyOverlayAbTestSettings/applyOverlayAbTestSettings'
import { getDefaultProductCardSettings } from 'product-list/utils/getDefaultProductCardSettings/getDefaultProductCardSettings'
import { useMemo } from 'react'
import { AbTestKeys } from 'types/constants/AbTestKeys'

type Props = {
	readonly catalogItemsData: Product[]
	readonly filtersData: UseFiltersData
	readonly groups: ProductListGroup[]
	readonly listName: string
	readonly processedFilters: ProductListParams
	readonly rootCatalogPathname: string
	readonly serverProducts: RenderedProductListItem[]
}

export function List({
	catalogItemsData,
	filtersData,
	groups,
	listName,
	processedFilters,
	rootCatalogPathname,
	serverProducts,
}: Props) {
	const {
		country: { hasOnlineSale },
		abTestPage,
	} = useMasterData()

	const catalogProductCardSettings = useMemo(() => {
		const result = getDefaultProductCardSettings({
			hasOnlineSale,
		})

		if (abTestPage?.enabled && abTestPage?.FFKey === AbTestKeys.PlpA2bPhase4) {
			applyOverlayAbTestSettings(result)
		}

		return result
	}, [hasOnlineSale, abTestPage])

	const { defaultView, currentView, setCurrentView } =
		usePlpProductListProvider()

	const filters = useMemo<ProductListFilters>(
		() => ({
			filtersData,
			resultPathname: rootCatalogPathname,
			initialParams: processedFilters,
			enabled: true,
		}),
		[filtersData, rootCatalogPathname, processedFilters]
	)

	return (
		<ProductListAnalyticsProvider
			currentView={currentView}
			events={productListEvents}
		>
			<ProductList
				filters={filters}
				responsiveProductCardSettings={catalogProductCardSettings}
				defaultView={defaultView}
				displayViewSelector
				groups={groups}
				name={listName}
				preLoadedProductsData={catalogItemsData}
				serverProducts={serverProducts}
				onViewChange={setCurrentView}
			/>
		</ProductListAnalyticsProvider>
	)
}
